import type { TilePosition } from '../../model/gamestate/GameState';
import type { InteractionManager } from './InteractionManager';
import type { MapLayerRenderer } from './MapLayerRenderer';
import type { EntityManager } from './EntityManager';
import type { HoveredActorInfo, InterpolatedActorData, InterpolatedEntityState, PixelPosition } from './types';
import type { Viewport } from './viewport';
import { getViewportScreenBounds, type ScreenBounds } from './culling';
import { ActiveRangeOverlayRenderer } from './ui/ActiveRangeOverlayRenderer';
import { PlacementPreviewRenderer } from './ui/PlacementPreviewRenderer';
import { QueueRenderer } from './ui/QueueRenderer';
import { ActorRenderer } from './ui/ActorRenderer';
import { WorldMarkerRenderer } from './ui/WorldMarkerRenderer';
import { HoverTooltipRenderer } from './ui/HoverTooltipRenderer';
import { EntityContextMenuController } from './ui/EntityContextMenuController';

export class UIRenderer {
    private readonly activeRangeOverlayRenderer: ActiveRangeOverlayRenderer;
    private readonly placementPreviewRenderer: PlacementPreviewRenderer;
    private readonly queueRenderer: QueueRenderer;
    private readonly actorRenderer: ActorRenderer;
    private readonly worldMarkerRenderer: WorldMarkerRenderer;
    private readonly hoverTooltipRenderer: HoverTooltipRenderer;
    private readonly entityContextMenuController: EntityContextMenuController;

    constructor(
        private readonly container: HTMLElement,
        private readonly canvas: HTMLCanvasElement,
        private readonly tileSize: number,
        private readonly eventTarget: EventTarget,
        private readonly getActiveRadius: () => number,
        private readonly requestRender: () => void,
    ) {
        this.activeRangeOverlayRenderer = new ActiveRangeOverlayRenderer(tileSize, getActiveRadius);
        this.placementPreviewRenderer = new PlacementPreviewRenderer(tileSize, requestRender);
        this.queueRenderer = new QueueRenderer();
        this.actorRenderer = new ActorRenderer(tileSize, getActiveRadius);
        this.worldMarkerRenderer = new WorldMarkerRenderer(tileSize);
        this.hoverTooltipRenderer = new HoverTooltipRenderer(canvas);
        this.entityContextMenuController = new EntityContextMenuController(container, canvas, eventTarget);

        this.placementPreviewRenderer.registerListeners(this.eventTarget);
        this.entityContextMenuController.registerListeners();
    }

    public destroy(): void {
        this.placementPreviewRenderer.destroy(this.eventTarget);
        this.entityContextMenuController.destroy();
    }

    public setActiveRangeCenter(position: TilePosition | null): void {
        this.activeRangeOverlayRenderer.setActiveRangeCenter(position);
    }

    public getActiveRangeCenter(): TilePosition | null {
        return this.activeRangeOverlayRenderer.getActiveRangeCenter();
    }

    public setQueuePreview(queue: (TilePosition | null)[]): void {
        this.queueRenderer.setQueuePreview(queue);
    }

    public drawActiveTiles(ctx: CanvasRenderingContext2D, mapLayer: MapLayerRenderer, viewport: Viewport): void {
        this.activeRangeOverlayRenderer.drawActiveTiles(ctx, mapLayer, viewport);
    }

    public drawHoverHighlight(ctx: CanvasRenderingContext2D, interactionInfo: { tile: TilePosition | null; inFog: boolean }, mapLayer: MapLayerRenderer, viewport: Viewport): void {
        this.activeRangeOverlayRenderer.drawHoverHighlight(ctx, interactionInfo, mapLayer, viewport);
    }

    public drawMapObjectPlacementPreview(
        ctx: CanvasRenderingContext2D,
        interactionInfo: { tile: TilePosition | null; inFog: boolean },
        viewport: Viewport,
        mapLayer: MapLayerRenderer,
    ): void {
        this.placementPreviewRenderer.drawMapObjectPlacementPreview(ctx, interactionInfo, viewport, mapLayer, this.getViewportBounds(this.tileSize * 2));
    }

    public drawPathTilePlacementPreview(
        ctx: CanvasRenderingContext2D,
        interactionInfo: { tile: TilePosition | null; inFog: boolean },
        viewport: Viewport,
        mapLayer: MapLayerRenderer,
    ): void {
        this.placementPreviewRenderer.drawPathTilePlacementPreview(ctx, interactionInfo, viewport, mapLayer);
    }

    public drawBiomeRegionPlacementPreview(
        ctx: CanvasRenderingContext2D,
        viewport: Viewport,
        mapLayer: MapLayerRenderer,
    ): void {
        this.placementPreviewRenderer.drawBiomeRegionPlacementPreview(ctx, mapLayer, viewport);
    }

    public drawPathLines(ctx: CanvasRenderingContext2D, selfState: InterpolatedEntityState<InterpolatedActorData> | null, _lastCharacterPosition: TilePosition | null, viewport: Viewport, mapLayer: MapLayerRenderer): void {
        this.queueRenderer.drawPathLines(ctx, selfState, viewport, mapLayer);
    }

    public drawQueueMarkers(ctx: CanvasRenderingContext2D, _selfState: InterpolatedEntityState<InterpolatedActorData> | null, _lastCharacterPosition: TilePosition | null, viewport: Viewport, mapLayer: MapLayerRenderer): void {
        this.queueRenderer.drawQueueMarkers(ctx, viewport, mapLayer);
    }

    public drawActors(ctx: CanvasRenderingContext2D, entityManager: EntityManager, lastCharacterPosition: TilePosition | null, viewport: Viewport, mapLayer: MapLayerRenderer): boolean {
        return this.actorRenderer.drawActors(ctx, entityManager, lastCharacterPosition, viewport, mapLayer, this.getViewportBounds(this.tileSize * 2.5));
    }

    public drawVisualAssets(
        ctx: CanvasRenderingContext2D,
        assets: Array<{ assetId: string; mapX: number; mapY: number }>,
        viewport: Viewport,
        _mapLayer: MapLayerRenderer
    ): void {
        this.worldMarkerRenderer.drawVisualAssets(ctx, assets, viewport, this.getViewportBounds(this.tileSize * 2));
    }

    public drawNpcs(ctx: CanvasRenderingContext2D, npcs: any[], viewport: Viewport, mapLayer: MapLayerRenderer): void {
        this.worldMarkerRenderer.drawNpcs(ctx, npcs, viewport, mapLayer, this.getViewportBounds(this.tileSize * 2));
    }

    public drawLootChests(ctx: CanvasRenderingContext2D, chests: { x: number; y: number }[], viewport: Viewport, mapLayer: MapLayerRenderer): void {
        this.worldMarkerRenderer.drawLootChests(ctx, chests, viewport, mapLayer, this.getViewportBounds(this.tileSize));
    }

    public drawHoveredActorTooltip(ctx: CanvasRenderingContext2D, interactionManager: InteractionManager): void {
        this.hoverTooltipRenderer.drawHoveredActorTooltip(ctx, interactionManager);
    }

    public showEntityContextMenu(pointer: PixelPosition, actor: HoveredActorInfo): void {
        this.entityContextMenuController.showEntityContextMenu(pointer, actor);
    }

    public hideEntityContextMenu(): void {
        this.entityContextMenuController.hideEntityContextMenu();
    }

    private getViewportBounds(margin = 0): ScreenBounds {
        return getViewportScreenBounds(this.canvas, margin);
    }
}
